import React, { JSX, ReactNode, useState } from 'react';

import { IconV2 } from '~/src/dash/components/icons/IconV2';
import { borderRadius, Col, Cols, spacing, Span, Text } from '~/src/dash/publicaUI';
import { publicaColors } from '~/src/dash/utils/colors';

export const CustomExtendedIDTooltip = (): NonNullable<ReactNode> | undefined => (
  <>
    <Text small color={publicaColors.white}>
      <Span tKey='ExtendedIDs' bold />: <Span tKey='TooltipExtendedIDTargetingTypes' />
    </Text>
    <ul>
      <li>
        <Text small color={publicaColors.white}>
          <Span tKey='Disabled' bold />: <Span tKey='TooltipExtendedIDTargetingTypeDisabled' />
        </Text>
      </li>
      <li>
        <Text small color={publicaColors.white}>
          <Span tKey='TargetIfPresent' bold />:{' '}
          <Span tKey='TooltipExtendedIDTargetingTypeTargetIfPresent' />
        </Text>
      </li>
      <li>
        <Text small color={publicaColors.white}>
          <Span tKey='TargetIfMissing' bold />:{' '}
          <Span tKey='TooltipExtendedIDTargetingTypeTargetIfMissing' />
        </Text>
      </li>
    </ul>
  </>
);

// currently not localized due to complexity
export const CustomAdvancedTargetingTooltip = (): NonNullable<ReactNode> | undefined => (
  <>
    <Text small color={publicaColors.white}>
      You can enter an exact value or use advanced patterns for more flexible targeting, can be
      comma separated.
    </Text>
    <ul style={{ marginBlock: 0, paddingTop: spacing.small }}>
      <li>
        <Text small color={publicaColors.white}>
          <Span bold>Exact Match: </Span> Enter a specific value to bid only on inventory that
          matches it exactly.
        </Text>
      </li>
      <li>
        <Text small color={publicaColors.white}>
          <Span bold>Advanced Targeting Patterns</Span>:
        </Text>
        <ul style={{ marginBlock: 0 }}>
          <li>
            <Text small color={publicaColors.white}>
              <Span bold>{'{{contain:value}}'}: </Span> Target inventory where the field's text
              contains the value you enter. Example: To target inventory about cars, enter{' '}
              <Span bold>{'{{contain:car}}'}</Span>
            </Text>
          </li>
          <li>
            <Text small color={publicaColors.white}>
              <Span bold>{'{{min:max}}'}: </Span> Target a numeric range. Bids will only be placed
              on inventory where the field's value falls between the minimum and maximum values
              (inclusive). Example: To target ages 25-35, enter <Span bold>{'{{25:35}}'}</Span>
            </Text>
          </li>
          <li>
            <Text small color={publicaColors.white}>
              <Span bold>{'{{null}}'}: </Span> Target inventory where the field is empty or missing.
              This pattern cannot be combined with any other logic. Example: To bid on inventory
              that's missing a user ID, enter <Span bold>{'{{null}}'}</Span>
            </Text>
          </li>
        </ul>
      </li>
    </ul>
  </>
);

export const StaticExtendedIDTooltip = (): JSX.Element => {
  const [isExpanded, setIsExpanded] = useState(false);
  return (
    <div style={{ backgroundColor: publicaColors.lighterBlue, borderRadius: borderRadius.small }}>
      <div style={{ padding: spacing.base }}>
        <Cols
          gap={spacing.small}
          style={{ alignItems: 'center', cursor: 'pointer' }}
          onClick={() => setIsExpanded(!isExpanded)}
        >
          <Col displayFlex>
            <IconV2 small name='info' color={publicaColors.veryDarkGray} />
          </Col>
          <Col displayFlex flex>
            <Text small singleLineLabel>
              <Span tKey='ExtendedIDs' bold />: <Span tKey='TooltipExtendedIDTargetingTypes' />
            </Text>
          </Col>
          <Col displayFlex>
            <IconV2
              small
              name='caret'
              color={publicaColors.veryDarkGray}
              style={{ transform: isExpanded ? 'rotate(180deg)' : 'rotate(0deg)' }}
            />
          </Col>
        </Cols>
        {isExpanded && (
          <ul style={{ marginBlock: 0, paddingTop: spacing.small }}>
            <li>
              <Text small>
                <Span tKey='Disabled' bold />:{' '}
                <Span tKey='TooltipExtendedIDTargetingTypeDisabled' />
              </Text>
            </li>
            <li>
              <Text small>
                <Span tKey='TargetIfPresent' bold />:{' '}
                <Span tKey='TooltipExtendedIDTargetingTypeTargetIfPresent' />
              </Text>
            </li>
            <li>
              <Text small>
                <Span tKey='TargetIfMissing' bold />:{' '}
                <Span tKey='TooltipExtendedIDTargetingTypeTargetIfMissing' />
              </Text>
            </li>
          </ul>
        )}
      </div>
    </div>
  );
};

// currently not localized due to complexity
export const StaticAdvancedTargetingTooltip = (): NonNullable<ReactNode> | undefined => {
  const [isExpanded, setIsExpanded] = useState(false);
  return (
    <div style={{ backgroundColor: publicaColors.lighterBlue, borderRadius: borderRadius.small }}>
      <div style={{ padding: spacing.base }}>
        <Cols
          gap={spacing.small}
          style={{ alignItems: 'center', cursor: 'pointer' }}
          onClick={() => setIsExpanded(!isExpanded)}
        >
          <Col displayFlex>
            <IconV2 small name='info' color={publicaColors.veryDarkGray} />
          </Col>
          <Col displayFlex flex>
            <Text small singleLineLabel>
              You can enter an exact value or use advanced patterns for more flexible targeting, can
              be comma separated.
            </Text>
          </Col>
          <Col displayFlex>
            <IconV2
              small
              name='caret'
              color={publicaColors.veryDarkGray}
              style={{ transform: isExpanded ? 'rotate(180deg)' : 'rotate(0deg)' }}
            />
          </Col>
        </Cols>
        {isExpanded && (
          <>
            <ul style={{ marginBlock: 0, paddingTop: spacing.small }}>
              <li>
                <Text small>
                  <Span bold>Exact Match: </Span> Enter a specific value to bid only on inventory
                  that matches it exactly.
                </Text>
              </li>
              <li>
                <Text small>
                  <Span bold>Advanced Targeting Patterns</Span>:
                </Text>
                <ul style={{ marginBlock: 0 }}>
                  <li>
                    <Text small>
                      <Span bold>{'{{contain:value}}'}: </Span> Target inventory where the field's
                      text contains the value you enter. Example: To target inventory about cars,
                      enter <Span bold>{'{{contain:car}}'}</Span>
                    </Text>
                  </li>
                  <li>
                    <Text small>
                      <Span bold>{'{{min:max}}'}: </Span> Target a numeric range. Bids will only be
                      placed on inventory where the field's value falls between the minimum and
                      maximum values (inclusive). Example: To target ages 25-35, enter{' '}
                      <Span bold>{'{{25:35}}'}</Span>
                    </Text>
                  </li>
                  <li>
                    <Text small>
                      <Span bold>{'{{null}}'}: </Span> Target inventory where the field is empty or
                      missing. This pattern cannot be combined with any other logic. Example: To bid
                      on inventory that's missing a user ID, enter <Span bold>{'{{null}}'}</Span>
                    </Text>
                  </li>
                </ul>
              </li>
            </ul>
          </>
        )}
      </div>
    </div>
  );
};
